import type Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import Router from '@ember/routing/router';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import {
  annualGoalTypeId,
  journalGoalLabels,
  monthlyGoalTypeId,
  oneTimeGoalTypeId,
} from 'donor-elf-web/lib/journal-goal-utils';
import BulkActionModel, {
  type BulkActionResponseDuplicateJournal,
} from 'donor-elf-web/models/bulk-action';
import JournalCreateModel from 'donor-elf-web/models/journal-create';
import JournalTemplateModel from 'donor-elf-web/models/journal-template';
import type { JournalAddRouteModel } from 'donor-elf-web/routes/funds/fund/journals/add';
import CampaignService from 'donor-elf-web/services/campaign-service';
import CurrentUser from 'donor-elf-web/services/current-user';
import JournalService from 'donor-elf-web/services/journal-service';
import journalCreateValidation from 'donor-elf-web/validations/journal-create';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import moment from 'moment';

export default class JournalsAddController extends Controller {
  @service declare currentUser: CurrentUser;
  @service declare journalService: JournalService;
  @service declare router: Router;
  @service declare campaignService: CampaignService;
  @service declare store: Store;

  @tracked showValidationErrors = false;
  @tracked saving = false;
  @tracked error: any;
  @tracked changeset: any;
  journalCreate?: JournalCreateModel;
  @tracked duplicateJournalBulkAction?: BulkActionModel;
  @tracked duplicateJournalId?: string;
  @tracked setClosedDateOnDuplicateJournal = false;

  queryParams = ['duplicateJournalId'];

  model!: JournalAddRouteModel;

  get campaignSelectOptions(): SelectOption[] {
    return [
      { val: undefined, label: 'None' },
      ...this.campaignService.sortedCampaignSelectOptions,
    ];
  }

  get isDuplicatingJournal() {
    return this.duplicateJournalId != null;
  }

  get duplicateJournalHasNoClosedDate() {
    return this.model.duplicateJournalInfo?.journal.closedDate == null;
  }

  get multipleTemplatesAvailable() {
    return this.model.templates.length > 1;
  }

  get isUniversityChristianOutreachUser() {
    return this.currentUser.organizationId === '239';
  }

  get showCampaignDropDown() {
    const { campaignsEnabled, campaigns } = this.campaignService;
    return campaignsEnabled && campaigns.length > 0;
  }

  get templateOptions(): SelectOption[] {
    // templates with a fundGroupId are first
    let sortedTemplates = this.model.templates
      .slice()
      .sort((a: JournalTemplateModel) => (isPresent(a.fundGroupId) ? -1 : 0));

    return [
      {
        val: undefined,
        label: '',
      } as SelectOption,
    ].concat(
      sortedTemplates.map((x) => {
        return {
          val: x.id,
          label: x.name,
        };
      }),
    );
  }

  get templateIsSelectedOrDuplicatingJournal() {
    return this.selectedTemplate != null || this.isDuplicatingJournal;
  }

  get selectedTemplate() {
    return this.model.templates.find(
      (x) => x.id === this.changeset?.get('journalTemplateId'),
    );
  }

  get canSelectMainGoalType() {
    return (
      !this.isDuplicatingJournal &&
      this.selectedTemplate?.mainGoalTypeId == null
    );
  }

  get showDecisionsRequireGift() {
    let { selectedTemplate } = this;
    return (
      selectedTemplate &&
      !selectedTemplate.hideDecisionsRequireGift &&
      !selectedTemplate.decisionsRequireGift
    );
  }

  get canHaveSecondaryOneTimeGoal() {
    return (
      this.selectedTemplate?.canHaveSecondaryOneTimeGoal &&
      this.changeset.get('mainGoalTypeId') !== oneTimeGoalTypeId
    );
  }

  get mainGoalTypeOptions() {
    return Object.keys(journalGoalLabels)
      .filter((x) => Number(x) !== annualGoalTypeId)
      .map((goalTypeId) => {
        return {
          // @ts-expect-error
          label: journalGoalLabels[goalTypeId],
          val: Number(goalTypeId),
        };
      });
  }

  get mainGoalTypeLabel() {
    if (this.canSelectMainGoalType || this.isDuplicatingJournal) {
      return 'Goal Amount';
    } else {
      let mainGoalTypeLabel =
        journalGoalLabels[this.changeset.get('mainGoalTypeId')];
      return `${mainGoalTypeLabel} Goal Amount`.trim();
    }
  }

  @action
  onJournalTemplateChanged() {
    let { selectedTemplate } = this;
    if (selectedTemplate) {
      // if you update this code block, then update the code block in createChangeset() that sets the same fields
      this.changeset.set(
        'mainGoalTypeId',
        selectedTemplate.mainGoalTypeId || monthlyGoalTypeId,
      );
      this.changeset.set(
        'decisionsRequireGift',
        selectedTemplate.decisionsRequireGift,
      );
    }
  }

  @action
  templateDidInsert() {
    this.createChangeset();
  }

  @action
  templateWillDestroy() {
    this.showValidationErrors = false;
    this.saving = false;
    this.error = undefined;
    this.changeset = undefined;
    this.journalCreate = undefined;
    this.duplicateJournalBulkAction = undefined;
    this.duplicateJournalId = undefined;
  }

  createChangeset() {
    let today = moment();
    this.journalCreate = this.store.createRecord('journal-create', {
      startDate: today.toDate(),
      goalDate: moment().add(6, 'months').toDate(),
      active: true,
    });
    if (this.isDuplicatingJournal) {
      this.journalCreate.duplicateJournalId =
        this.model.duplicateJournalInfo!.journal.id;
      this.journalCreate.mainGoalTypeId =
        this.model.duplicateJournalInfo!.goals![0].goalTypeId;
      this.journalCreate.duplicateMoveUnfinishedTasks = true;
      if (this.model.duplicateJournalInfo!.journal.closedDate == null) {
        this.setClosedDateOnDuplicateJournal = true;
        this.journalCreate.duplicateJournalClosedDate = today.toDate();
      } else {
        this.setClosedDateOnDuplicateJournal = false;
      }
    } else if (this.model.templates.length === 1) {
      let template = this.model.templates[0];
      this.journalCreate.journalTemplateId = template.id;
      this.journalCreate.mainGoalTypeId =
        template.mainGoalTypeId || monthlyGoalTypeId;
      this.journalCreate.decisionsRequireGift = template.decisionsRequireGift;
    }
    if (this.isUniversityChristianOutreachUser) {
      this.journalCreate.mainGoalTypeId = annualGoalTypeId;
      this.journalCreate.mainGoalAmount = 1; // it doesn't matter since they just show the dashboard goal
    }
    this.changeset = new Changeset(
      this.journalCreate,
      lookupValidator(journalCreateValidation),
      journalCreateValidation,
    ) as any;
  }

  @action
  toggleSetClosedDateOnDuplicateJournal(e: any) {
    this.setClosedDateOnDuplicateJournal = e.target.checked;
    if (this.setClosedDateOnDuplicateJournal) {
      this.changeset.set('duplicateJournalClosedDate', moment().toDate());
    } else {
      this.changeset.set('duplicateJournalClosedDate', undefined);
    }
  }

  @action
  async save() {
    this.saving = true;
    let { changeset } = this;
    try {
      await changeset.validate();
      if (changeset.get('isValid')) {
        await changeset.save();

        if (this.journalCreate!.duplicateJournalBulkAction != null) {
          this.duplicateJournalBulkAction =
            this.journalCreate!.duplicateJournalBulkAction;
        } else {
          await this.journalService.load({ forceReload: true });
          this.router.transitionTo(
            'funds.fund.journals.journal.add-contacts',
            changeset.get('id'),
          );
        }
      } else {
        this.showValidationErrors = true;
      }
    } catch (error) {
      this.error = error;
    }
    this.saving = false;
  }

  @action
  async onDuplicateJournalBulkActionStatusChange(bulkAction: BulkActionModel) {
    if (bulkAction.status === 'completed') {
      await this.journalService.load({ forceReload: true });
      this.router.transitionTo(
        'funds.fund.journals.journal',
        (bulkAction.response! as BulkActionResponseDuplicateJournal)
          .new_journal_id,
      );
    }
  }
}
